"use client";

import { useGlassBorder } from "../custom/glass-border";
import { cn } from "../../lib/cn";
import {
	LANDING_BEAM_FROM,
	LANDING_BEAM_TO,
} from "../../lib/design-system/landing-beam-colors";
import { type MotionStyle, motion, type Transition } from "motion/react";
import {
	type CSSProperties,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react";

export interface BorderBeamProps {
	/**
	 * Width/height of the moving gradient square (px). Larger = longer color trail along the border.
	 * Capped dynamically to a fraction of the smaller host dimension so small layouts don’t clip or jump.
	 */
	size?: number;
	/**
	 * Corner radius for the `offset-path` rect (px). Must match the parent’s border radius,
	 * not the beam `size`; tying both to the same large value breaks small layouts and causes
	 * a jumping beam.
	 */
	pathCornerRadiusPx?: number;
	/**
	 * When true (inside {@link GlassBorder} `variant="inner"`), uses the measured inner clip
	 * radius from glass context so the beam matches `--glass-inner-radius`.
	 */
	pathCornerRadiusFromGlass?: boolean;
	/**
	 * Full loop duration in seconds.
	 */
	duration?: number;
	/**
	 * Delay in seconds (subtracted via transition delay).
	 */
	delay?: number;
	colorFrom?: string;
	colorTo?: string;
	transition?: Transition;
	className?: string;
	style?: CSSProperties;
	/**
	 * Whether to reverse the animation direction.
	 */
	reverse?: boolean;
	/**
	 * The initial offset position (0–100).
	 */
	initialOffset?: number;
	/**
	 * Border width in px for the masked track.
	 */
	borderWidth?: number;
	/**
	 * When true, uses `size` exactly (no layout cap). Prefer leaving false so the beam stays inside the ring.
	 */
	disableLayoutSizeCap?: boolean;
	/**
	 * Upper bound for beam size as a fraction of the host’s **shorter** inner edge (after a small corner slack).
	 * Default tuned for rounded rects; lower if you still see corner clipping.
	 */
	beamSizeLayoutCapFraction?: number;
	/**
	 * `perimeter` (default): beam follows the full rounded-rect border.
	 * `top` / `bottom`: beam runs along a **single horizontal edge** (left→right path; use `reverse` on one rail for opposite motion). Use with a short full-width strip (e.g. CTA edge accents); layout cap uses **width** so thin rails don’t shrink the beam to a dot.
	 */
	traverse?: "perimeter" | "top" | "bottom";
	/**
	 * Horizontal gradient axis for the moving bar (`to-l` = default, matches prior behavior; `to-r` mirrors the ramp for edge rails without changing motion — pair with {@link reverse} when you want motion + ramp both flipped).
	 */
	gradientTo?: "l" | "r";
}

/**
 * Max square size (px) that fits the masked border trail without dominating corners / short edges.
 */
function layoutCapForBeamBox(
	widthPx: number,
	heightPx: number,
	borderWidthPx: number,
	pathCornerRadiusPx: number,
	fraction: number,
): number {
	if (
		!(widthPx > 0 && heightPx > 0) ||
		!Number.isFinite(widthPx) ||
		!Number.isFinite(heightPx)
	) {
		return Number.POSITIVE_INFINITY;
	}
	const shortest = Math.min(widthPx, heightPx);
	/* Approximate how much of the short span is “lost” to rounded corners so the cap isn’t too aggressive on large radii. */
	const cornerSlack = Math.min(
		shortest * 0.2,
		Math.max(0, pathCornerRadiusPx) * 1.25,
	);
	const basis = Math.max(borderWidthPx * 3, shortest - cornerSlack);
	const capPx = Math.floor(basis * fraction);
	return Math.max(borderWidthPx * 2, capPx);
}

const DEFAULT_BEAM_LAYOUT_CAP_FRACTION = 0.48;

/**
 * Perimeter beams: max moving-square side length as a multiple of {@link pathCornerRadiusPx}.
 * Keeps the patch small vs corner arc length so `offset-rotate: auto` does not read as a corner jump.
 */
const PERIMETER_BEAM_CORNER_RADIUS_SIZE_FACTOR = 2.5;

/** Layout cap for a thin horizontal rail: scale with width so `size` isn’t crushed to ~borderWidth. */
function layoutCapForHorizontalEdge(
	widthPx: number,
	borderWidthPx: number,
	fraction: number,
): number {
	if (!(widthPx > 0) || !Number.isFinite(widthPx)) {
		return Number.POSITIVE_INFINITY;
	}
	const capPx = Math.floor(widthPx * fraction);
	return Math.max(borderWidthPx * 10, capPx);
}

/**
 * Animated gradient beam that travels along the border of a positioned parent.
 * Place inside a `relative overflow-hidden rounded-*` wrapper; the parent’s radius is inherited.
 */
export function BorderBeam({
	className,
	size = 50,
	pathCornerRadiusPx = 12,
	pathCornerRadiusFromGlass = false,
	delay = 0,
	duration = 6,
	colorFrom = LANDING_BEAM_FROM,
	colorTo = LANDING_BEAM_TO,
	transition,
	style,
	reverse = false,
	initialOffset = 0,
	borderWidth = 1,
	disableLayoutSizeCap = false,
	beamSizeLayoutCapFraction = DEFAULT_BEAM_LAYOUT_CAP_FRACTION,
	traverse = "perimeter",
	gradientTo = "l",
}: BorderBeamProps) {
	const trackRef = useRef<HTMLDivElement | null>(null);
	const [boxPx, setBoxPx] = useState<{ w: number; h: number } | null>(null);
	const glass = useGlassBorder();
	const resolvedPathCornerRadiusPx =
		pathCornerRadiusFromGlass && glass !== null
			? glass.innerCornerRadiusPx
			: pathCornerRadiusPx;

	useLayoutEffect(() => {
		const el = trackRef.current;
		if (!el) {
			return;
		}

		function measure() {
			const node = trackRef.current;
			if (!node) {
				return;
			}
			const r = node.getBoundingClientRect();
			setBoxPx({ w: r.width, h: r.height });
		}

		measure();
		const ro = new ResizeObserver(() => {
			measure();
		});
		ro.observe(el);
		return () => {
			ro.disconnect();
		};
	}, []);

	const resolvedSize = useMemo(() => {
		const isEdgeTraverseMemo = traverse === "top" || traverse === "bottom";
		const fromLayout = disableLayoutSizeCap
			? size
			: Math.min(
					size,
					boxPx === null
						? Number.POSITIVE_INFINITY
						: isEdgeTraverseMemo
							? layoutCapForHorizontalEdge(
									boxPx.w,
									borderWidth,
									beamSizeLayoutCapFraction,
								)
							: layoutCapForBeamBox(
									boxPx.w,
									boxPx.h,
									borderWidth,
									resolvedPathCornerRadiusPx,
									beamSizeLayoutCapFraction,
								),
				);

		const cornerRadiusMaxPx =
			!isEdgeTraverseMemo &&
			resolvedPathCornerRadiusPx > 0 &&
			Number.isFinite(resolvedPathCornerRadiusPx)
				? Math.floor(
						PERIMETER_BEAM_CORNER_RADIUS_SIZE_FACTOR *
							resolvedPathCornerRadiusPx,
					)
				: Number.POSITIVE_INFINITY;

		const merged = Math.min(fromLayout, cornerRadiusMaxPx);
		return Math.max(borderWidth * 2, merged);
	}, [
		beamSizeLayoutCapFraction,
		borderWidth,
		boxPx,
		disableLayoutSizeCap,
		resolvedPathCornerRadiusPx,
		size,
		traverse,
	]);

	const isEdgeTraverse = traverse === "top" || traverse === "bottom";
	const gradientAxisClass =
		gradientTo === "r" ? "bg-gradient-to-r" : "bg-gradient-to-l";

	const offsetPath = isEdgeTraverse
		? undefined
		: `rect(0 auto auto 0 round ${resolvedPathCornerRadiusPx}px)`;

	/** Match visible stroke of perimeter {@link BorderBeam} (masked border ring ≈ {@link borderWidth}). */
	const edgeBeamHeightPx = Math.max(1, borderWidth);

	const trackWidthPx = boxPx?.w ?? 0;
	const edgeMotionReady = isEdgeTraverse && trackWidthPx > 0;

	const edgeInitialX = reverse ? trackWidthPx : -resolvedSize;
	const edgeTargetX = reverse ? -resolvedSize : trackWidthPx;

	return (
		<div
			ref={trackRef}
			className={cn(
				"pointer-events-none absolute inset-0 rounded-[inherit]",
				isEdgeTraverse
					? "overflow-hidden"
					: "border-(length:--border-beam-width) border-transparent [mask-image:linear-gradient(transparent,transparent),linear-gradient(#000,#000)] [mask-composite:intersect] [mask-clip:padding-box,border-box]",
			)}
			style={
				isEdgeTraverse
					? undefined
					: ({
							"--border-beam-width": `${borderWidth}px`,
						} as CSSProperties)
			}
		>
			<motion.div
				className={cn(
					"absolute",
					traverse === "perimeter" && "aspect-square",
					`${gradientAxisClass} from-[var(--color-from)] via-[var(--color-to)] to-transparent`,
					className,
				)}
				style={
					{
						width: resolvedSize,
						...(isEdgeTraverse
							? {
									height: edgeBeamHeightPx,
									top: "50%",
									left: 0,
									y: "-50%",
								}
							: {}),
						...(offsetPath ? { offsetPath } : {}),
						"--color-from": colorFrom,
						"--color-to": colorTo,
						...style,
					} as MotionStyle
				}
				initial={
					isEdgeTraverse
						? { x: edgeInitialX }
						: { offsetDistance: `${initialOffset}%` }
				}
				animate={
					isEdgeTraverse
						? edgeMotionReady
							? { x: [edgeInitialX, edgeTargetX] }
							: { x: edgeInitialX }
						: {
								offsetDistance: reverse
									? [
											`${100 - initialOffset}%`,
											`${-initialOffset}%`,
										]
									: [
											`${initialOffset}%`,
											`${100 + initialOffset}%`,
										],
							}
				}
				transition={{
					ease: "linear",
					duration,
					delay: -delay,
					...transition,
					repeat: isEdgeTraverse
						? edgeMotionReady
							? Number.POSITIVE_INFINITY
							: 0
						: Number.POSITIVE_INFINITY,
				}}
			/>
		</div>
	);
}
